import type { MouseEvent } from "react";
import { getGoogleLoginUrl } from "../../api/auth.api";

const scrollTo = (id: string) => (e: MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how-it-works" },
];

const FEATURES = [
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
      </svg>
    ),
    title: "Upload Any File Type",
    description: "Documents, photos, videos, code, audio. TitanCloud stores everything without restrictions on file type.",
  },
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
      </svg>
    ),
    title: "Instant File Preview",
    description: "Preview images, PDFs, videos, audio, and code files directly in the browser. No download needed.",
  },
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
      </svg>
    ),
    title: "Organized Folder Structure",
    description: "Create nested folders to keep your files organized. Move, rename, and manage your storage exactly how you want.",
  },
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803a7.5 7.5 0 0010.607 0z" />
      </svg>
    ),
    title: "Powerful Search",
    description: "Find any file instantly with full-text search across all your files and folders in one keystroke.",
  },
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
    title: "Private and Secure",
    description: "Your files stay completely private. No data mining, no ads, no sharing with anyone.",
  },
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
      </svg>
    ),
    title: "Trash and Restore",
    description: "Accidentally deleted a file? TitanCloud keeps deleted files in trash so you can restore them any time.",
  },
];

const STEPS = [
  {
    number: "01",
    title: "Sign In and Request Access",
    description: "Sign in with your Google account and get 100MB of free storage to start testing the app right away.",
  },
  {
    number: "02",
    title: "Upload Your Files",
    description: "Drag and drop files or use the upload button. Organize into folders as you go. Your quota is shown in real time.",
  },
  {
    number: "03",
    title: "Need More Storage?",
    description: "Enjoying TitanCloud? Send us an email and we will increase your storage quota so you can keep everything in one place.",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-gray-800/60 bg-gray-950/80 backdrop-blur-md">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-2.5" aria-label="TitanCloud home">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600">
              <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H5.5z" />
              </svg>
            </div>
            <span className="text-lg font-semibold tracking-tight">TitanCloud</span>
          </a>

          <div className="hidden items-center gap-8 md:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={scrollTo(link.href.slice(1))}
                className="text-sm text-gray-400 transition-colors hover:text-white"
              >
                {link.label}
              </a>
            ))}
          </div>

          <a
            href={getGoogleLoginUrl()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
          >
            Sign In
          </a>
        </nav>
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden px-6 pb-24 pt-20 text-center md:pt-32">
          {/* Background glow */}
          <div className="pointer-events-none absolute inset-0 flex items-start justify-center" aria-hidden="true">
            <div className="h-[500px] w-[800px] rounded-full bg-indigo-600/10 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-4 py-1.5 text-sm text-indigo-300">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
              Private cloud storage, invite only
            </div>

            <h1 className="mb-6 text-4xl font-bold leading-tight tracking-tight md:text-6xl">
              Your Private Cloud.{" "}
              <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
                No Compromises.
              </span>
            </h1>

            <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-gray-400">
              TitanCloud is a secure, private cloud storage platform for storing files, photos, and documents.
              Fast uploads, instant previews, and powerful search all in a clean, simple interface.
            </p>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href={getGoogleLoginUrl()}
                className="flex items-center gap-3 rounded-xl bg-indigo-600 px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-indigo-600/20 transition-all hover:bg-indigo-500 hover:shadow-indigo-500/30"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Get Started with Google
              </a>
              <a
                href="#features"
                onClick={scrollTo("features")}
                className="rounded-xl border border-gray-700 px-6 py-3.5 text-base font-medium text-gray-300 transition-colors hover:border-gray-500 hover:text-white"
              >
                See features
              </a>
            </div>

            {/* Free storage note */}
            <p className="mt-5 text-sm text-gray-500">
              Start with <span className="font-medium text-gray-300">100MB free storage</span> to test the app.
              Need more? Just send us an email and we will increase your quota.
            </p>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-y border-gray-800/60 bg-gray-900/40 px-6 py-8">
          <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-10 text-sm text-gray-400">
            {[
              { icon: "🔒", text: "End-to-end private storage" },
              { icon: "⚡", text: "Fast global CDN via Vercel" },
              { icon: "📁", text: "Any file type, any size" },
              { icon: "🛡️", text: "100% private, always" },
              { icon: "🌐", text: "Works in any browser" },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <span aria-hidden="true">{icon}</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section id="features" className="px-6 py-24">
          <div className="mx-auto max-w-6xl">
            <div className="mb-16 text-center">
              <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl">
                Everything you need to store and manage files
              </h2>
              <p className="mx-auto max-w-2xl text-gray-400">
                TitanCloud gives you a complete private cloud file storage experience. Upload, organize, preview, and access your files securely.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((feature) => (
                <article
                  key={feature.title}
                  className="rounded-2xl border border-gray-800 bg-gray-900/50 p-6 transition-colors hover:border-gray-700"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-600/10 text-indigo-400">
                    {feature.icon}
                  </div>
                  <h3 className="mb-2 text-base font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm leading-relaxed text-gray-400">{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="bg-gray-900/30 px-6 py-24">
          <div className="mx-auto max-w-4xl">
            <div className="mb-16 text-center">
              <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl">
                How TitanCloud works
              </h2>
              <p className="text-gray-400">
                Get set up and start storing your files in minutes.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {STEPS.map((step, i) => (
                <div key={step.number} className="relative text-center">
                  {i < STEPS.length - 1 && (
                    <div className="absolute left-1/2 top-6 hidden h-px w-full translate-x-1/2 bg-gradient-to-r from-gray-700 to-transparent md:block" aria-hidden="true" />
                  )}
                  <div className="relative z-10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-indigo-500/40 bg-gray-900 text-sm font-bold text-indigo-400">
                    {step.number}
                  </div>
                  <h3 className="mb-2 text-base font-semibold">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-gray-400">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-6 py-24 text-center">
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl">
              Ready to take control of your files?
            </h2>
            <p className="mb-3 text-gray-400">
              Sign in with Google, get 100MB of free storage, and start exploring TitanCloud right away.
            </p>
            <p className="mb-10 text-sm text-gray-500">
              Need more space? Send us an email and we will increase your quota.
            </p>
            <a
              href={getGoogleLoginUrl()}
              className="inline-flex items-center gap-3 rounded-xl bg-indigo-600 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-indigo-600/20 transition-all hover:bg-indigo-500"
            >
              Start Free with Google
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 px-6 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 text-sm text-gray-500 md:flex-row">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-indigo-600">
              <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H5.5z" />
              </svg>
            </div>
            <span className="font-medium text-gray-400">TitanCloud</span>
          </div>
          <p>© {new Date().getFullYear()} TitanCloud. Private cloud storage for power users.</p>
          <div className="flex gap-6">
            <a href="#features" onClick={scrollTo("features")} className="hover:text-gray-300 transition-colors">Features</a>
            <a href="#how-it-works" onClick={scrollTo("how-it-works")} className="hover:text-gray-300 transition-colors">How it works</a>
            <a href={getGoogleLoginUrl()} className="hover:text-gray-300 transition-colors">Sign In</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
